export class ConsentCancelledError extends Error {
	override readonly name = "ConsentCancelledError";

	constructor(message = "Consent cancelled") {
		super(message);
	}
}

export const isConsentCancelledError = (error: unknown): boolean => {
	if (error instanceof ConsentCancelledError) {
		return true;
	}
	if (!error || typeof error !== "object") {
		return false;
	}

	const record = error as {
		readonly name?: unknown;
		readonly message?: unknown;
		readonly errorCode?: unknown;
	};
	const name = typeof record.name === "string" ? record.name : "";
	const message = typeof record.message === "string" ? record.message : "";
	const errorCode = typeof record.errorCode === "string" ? record.errorCode : "";

	return (
		name === "ConsentCancelledError" ||
		/user_cancelled|user cancelled|popup_window_error|empty_window_error|access_denied|consent_required|closed|cancelled/i.test(
			`${errorCode} ${message}`
		)
	);
};

export const getErrorStatus = (error: unknown): number | null => {
	if (!error || typeof error !== "object") {
		return null;
	}

	const record = error as { readonly status?: unknown; readonly statusCode?: unknown };
	if (typeof record.status === "number") {
		return record.status;
	}
	if (typeof record.statusCode === "number") {
		return record.statusCode;
	}
	return null;
};

export const POPUP_CLOSE_SOURCE = "tg-popup-close" as const;

export type PopupCloseOutcome = "success" | "cancel" | "closed";

export type PopupCloseMessage = {
	readonly _source: typeof POPUP_CLOSE_SOURCE;
	readonly outcome: "success" | "cancel";
};

export const isPopupCloseMessage = (data: unknown): data is PopupCloseMessage => {
	if (!data || typeof data !== "object") {
		return false;
	}
	const record = data as { readonly _source?: unknown; readonly outcome?: unknown };
	if (record._source !== POPUP_CLOSE_SOURCE) {
		return false;
	}
	return record.outcome === "success" || record.outcome === "cancel";
};

const isPopupWindowClosed = (popup: Window): boolean => {
	try {
		return popup.closed;
	} catch {
		return true;
	}
};

/**
 * Wait until the popup reports success/cancel via postMessage, or the user closes it.
 * `closed` means the window went away without a message (user X, or a host page we do not control).
 */
export const waitForPopupClose = (wnd: Window): Promise<PopupCloseOutcome> =>
	new Promise((resolve) => {
		let settled = false;
		const finish = (outcome: PopupCloseOutcome): void => {
			if (settled) {
				return;
			}
			settled = true;
			window.clearInterval(timer);
			window.removeEventListener("message", onMessage);
			resolve(outcome);
		};
		const onMessage = (event: MessageEvent): void => {
			if (!isPopupCloseMessage(event.data)) {
				return;
			}
			if (event.source != null && event.source !== wnd) {
				return;
			}
			finish(event.data.outcome);
		};
		window.addEventListener("message", onMessage);
		const timer = window.setInterval(() => {
			if (isPopupWindowClosed(wnd)) {
				finish("closed");
			}
		}, 250);
		if (isPopupWindowClosed(wnd)) {
			finish("closed");
		}
	});

/** Resolve when popup closes. Used when the host page cannot postMessage (e.g. Microsoft admin consent). */
export const waitForWindowClose = async (wnd: Window): Promise<void> => {
	await waitForPopupClose(wnd);
};

export const openCenteredPopup = (input: {
	readonly url: string;
	readonly title: string;
	readonly width?: number;
	readonly height?: number;
}): Window | null => {
	const width = input.width ?? 480;
	const height = input.height ?? 640;
	const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - width) / 2));
	const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - height) / 2));
	return window.open(input.url, input.title, `popup=yes,width=${width},height=${height},left=${left},top=${top}`);
};
